import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import { tradingAccounts, type TradingAccount } from "@/api/tradingAccounts";
import { withdrawals, type WithdrawalCreate } from "@/api/withdrawals";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  FormError,
  Input,
  Label,
  Select,
} from "@/components/ui";

/**
 * Client withdrawal page.
 *
 * Lists the caller's withdrawal requests across all their trading
 * accounts. "New withdrawal" opens a modal that posts to
 * `/api/v1/withdrawals` — the request lands in the admin queue with
 * status PENDING. Caller can cancel a still-pending request.
 */
export function WithdrawalsPage() {
  const list = useQuery({ queryKey: ["withdrawals", "mine"], queryFn: () => withdrawals.listMine() });
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();

  const cancel = useMutation({
    mutationFn: (id: string) => withdrawals.cancel(id),
    onSuccess: () => {
      toast.push("Withdrawal canceled", "warn");
      qc.invalidateQueries({ queryKey: ["withdrawals"] });
    },
    onError: (e) =>
      toast.push(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e), "error"),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold text-term-fg">Withdrawals</h1>
        <Button onClick={() => setOpen(true)}>New withdrawal</Button>
      </div>

      <Card>
        <CardBody className="p-0">
          {list.isLoading ? (
            <div className="p-3 text-sm text-term-mute">Loading…</div>
          ) : !list.data || list.data.length === 0 ? (
            <EmptyState
              title="No withdrawals yet"
              description="Request a withdrawal — your org admin reviews + approves before funds move."
              action={<Button onClick={() => setOpen(true)}>New withdrawal</Button>}
            />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Created</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Amount</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Status</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Account</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Note</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((w) => (
                  <tr key={w.id} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 tnum text-term-dim whitespace-nowrap">
                      {new Date(w.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-1.5 text-right tnum text-term-fg">
                      {w.amount} {w.currency}
                    </td>
                    <td className="px-3 py-1.5">{statusBadge(w.status)}</td>
                    <td className="px-3 py-1.5 font-mono text-2xs text-term-dim">
                      {w.trading_account_id.slice(0, 8)}
                    </td>
                    <td className="px-3 py-1.5 text-2xs text-term-dim">
                      {w.note ?? "—"}
                      {w.decision_note && (
                        <div className="text-term-mute italic">› {w.decision_note}</div>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      {w.status === "PENDING" && (
                        <Button
                          variant="danger"
                          onClick={() => {
                            if (confirm("Cancel this withdrawal?")) cancel.mutate(w.id);
                          }}
                        >
                          Cancel
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {open && <NewWithdrawalModal onClose={() => setOpen(false)} />}
    </div>
  );
}


function statusBadge(s: string) {
  const tone =
    s === "APPROVED" || s === "PAID" ? "good"
      : s === "PENDING" ? "warn"
        : s === "REJECTED" || s === "CANCELED" ? "bad"
          : "neutral";
  return <Badge tone={tone as "good" | "bad" | "warn" | "neutral"}>{s}</Badge>;
}


function NewWithdrawalModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const accounts = useQuery({
    queryKey: ["trading-accounts", "mine"],
    queryFn: () => tradingAccounts.list(true),
  });
  const [form, setForm] = useState<WithdrawalCreate>({
    trading_account_id: "",
    amount: "",
    currency: "USD",
    note: "",
  });
  const [err, setErr] = useState<string | null>(null);

  const selectedAccount = accounts.data?.find(
    (a: TradingAccount) => a.id === form.trading_account_id,
  );

  function pickAccount(id: string) {
    const acct = accounts.data?.find((a: TradingAccount) => a.id === id);
    setForm((f) => ({
      ...f,
      trading_account_id: id,
      currency: acct?.base_currency ?? f.currency,
    }));
  }

  const submit = useMutation({
    mutationFn: () => withdrawals.request(form),
    onSuccess: () => {
      toast.push("Withdrawal requested", "success");
      qc.invalidateQueries({ queryKey: ["withdrawals"] });
      onClose();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  return (
    <Modal open onClose={onClose} title="Request withdrawal">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          submit.mutate();
        }}
        className="space-y-3"
      >
        <div>
          <Label>Trading account</Label>
          <Select
            value={form.trading_account_id}
            onChange={(e) => pickAccount(e.target.value)}
            required
          >
            <option value="">— pick —</option>
            {accounts.data?.map((a: TradingAccount) => (
              <option key={a.id} value={a.id}>
                {a.login_code}
                {a.label ? ` · ${a.label}` : ""} · balance {a.balance} {a.base_currency}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label>Amount{selectedAccount ? ` (${selectedAccount.base_currency})` : ""}</Label>
          <Input
            type="number"
            step="any"
            value={String(form.amount)}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            required
          />
          {selectedAccount && (
            <p className="mt-1 text-xs text-slate-500">
              Withdrawn in <strong>{selectedAccount.base_currency}</strong>, the
              account's base currency. FX conversion (if needed) happens on the
              treasury side.
            </p>
          )}
        </div>
        <div>
          <Label>Note (optional)</Label>
          <Input
            value={form.note ?? ""}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            placeholder="bank wire to ABC123"
          />
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={submit.isPending}>Request</Button>
        </div>
      </form>
    </Modal>
  );
}
